import { Component, OnInit, OnDestroy, Input, ViewChild } from "@angular/core";
import { Subscription } from "rxjs/Subscription";
import * as L from "leaflet";
import { point } from "@turf/helpers";
import { environment } from "../../../environments/environment";
import { Utils } from "../../common/utils";
import { MapBaseComponent } from "../../common/map-base/map-base.component";
import { ContextService } from "../../services/context.service";
import { IEvent, MatchStatus } from "../../services/match.service";
import { StatsService } from "../../services/stats.service";

import * as courtGeom from "../../../assets/data/court.json";
import * as zoneLinesGeom from "../../../assets/data/zone_lines.json";
import * as zonesGeom from "../../../assets/data/zone.json";
import { ISelectedPlayer } from "../players-list/players-list.component";

@Component({
  selector: "app-map-raw",
  templateUrl: "./map-raw.component.html",
  styleUrls: [
    "../../common/map-base/map-base.component.scss",
    "./map-raw.component.scss",
  ],
})
export class MapRawComponent
  extends MapBaseComponent
  implements OnInit, OnDestroy
{
  _events: IEvent[] = [];
  _teams: any = {};
  _playersSelected: ISelectedPlayer[] = [];

  @Input() get teams(): any {
    return this._teams;
  }
  set teams(teams: any) {
    this._teams = teams;
    this.indexTeams();
    this.refreshMap();
  }
  @Input() get events(): IEvent[] {
    return this._events;
  }
  set events(events: IEvent[]) {
    this._events = events;
    this.refreshMap(this.lastMessagePos);
    if (this.live) {
      this.showNotifications();
    } else {
      this.lastMessagePos = this._events.length;
    }
  }
  @Input() get playersSelected(): ISelectedPlayer[] {
    return this._playersSelected;
  }
  set playersSelected(playersSelected: ISelectedPlayer[]) {
    this._playersSelected = playersSelected.filter((elem) => elem);
    this.refreshMap(this.lastMessagePos);
  }
  @Input() live = false;

  @ViewChild("map") mapElem: any;

  map: any;
  markersLayer: any;
  markersList: any[] = [];

  teamsIndex: any = {};
  playersIndex: any = {};
  zoneStats: any;
  lastMessagePos: number;
  lastMessages: any[] = [];

  popupStats: any = {};
  iconShotPoint: any = null;
  iconShotFailed: any = null;

  utils = Utils;

  constructor(context: ContextService, statsService: StatsService) {
    super(statsService, context);
  }

  ngOnInit() {
    super.ngOnInit();
    this.refreshMap();
    this.initLayers();
  }

  private indexTeams() {
    this.teamsIndex = {};
    this.playersIndex = {};
    if (this.teams.home) {
      this.teamsIndex[this.teams.home.id] = {
        code: this.teams.home.code,
        name: this.teams.home.name,
      };
      this.playersIndex = this.teams.home.players.reduce((acum, elem) => {
        acum[elem.id] = elem;
        return acum;
      }, {});
    }
    if (this.teams.visitor) {
      this.teamsIndex[this.teams.visitor.id] = {
        code: this.teams.visitor.code,
        name: this.teams.visitor.name,
      };
      this.playersIndex = this.teams.visitor.players.reduce((acum, elem) => {
        acum[elem.id] = elem;
        return acum;
      }, this.playersIndex);
    }
  }

  /*
  drawBaseMap() {
    const courtGeomRotated = this.rotateGeomIfNeeded(courtGeom);
    const courtBaseFeature = courtGeomRotated.features[1];
    this.drawCourt(courtGeom, zoneLinesGeom);
  }
  */

  refreshMap(lastMessagePos = 0) {
    if (this.map && this._events.length && this._teams.home) {
      this.paintShots(this.lastMessagePos);
    }
  }

  initLayers() {
    this.markersLayer = L.layerGroup().addTo(this.map);
    this.iconShotPoint = L.divIcon({ className: `marker shot` });
    this.iconShotFailed = L.divIcon({ className: `marker shot failed` });
  }

  paintShots(lastMessagePos = 0) {
    if (!this.markersLayer) {
      this.initLayers();
    }
    this.clearMarkers();
    this._events.forEach((elem, idx) => {
      if (elem.x && elem.y && elem.shots) {
        if (
          (!this._playersSelected.length ||
            this._playersSelected.map((p) => p.id).indexOf(elem.player_0_id) !==
              -1) &&
          (this.context.getQuarter() === MatchStatus.Finished ||
            this.live ||
            elem.quarter === this.context.getQuarter())
        ) {
          const rotated = this.rotateGeomIfNeeded(point([elem.x, elem.y]));
          const live = this.live && idx >= lastMessagePos;
          const marker = L.marker(
            [rotated.geometry.coordinates[1], rotated.geometry.coordinates[0]],
            {
              icon: elem.failures
                ? this.iconShotFailed
                : L.divIcon({ className: `marker shot ${live ? "live" : ""}` }),
            }
          );
          marker.on("mouseover", (e) => {
            this.popupStats = this.generateMessages([elem], idx)[0];
            this.openPopup(e);
          });
          marker.on("mouseout", (e) => {
            this.closePopup(e);
          });
          this.markersLayer.addLayer(marker);
          this.markersList.push(marker);
        }
      }
    });
  }

  showNotifications() {
    let messages;
    messages = this._events.slice(this.lastMessagePos).slice(-5);
    this.lastMessages = this.generateMessages(messages, this.lastMessagePos);
    this.lastMessagePos = this._events.length;
  }

  generateMessages(messages: any[], fromPosition = 0) {
    const parsedMessages = messages.reduce((acum, elem, idx) => {
      if (elem.faults === 0) {
        const isHome = elem.team_0 === this.teams.home.id;
        const prevResult = this.getLastResult(fromPosition + idx);
        const message = {
          player: this.playersIndex[elem.player_0_id],
          team: this.teamsIndex[elem.team_0],
          points: elem.points || elem.failures,
          fail: !!elem.failures,
          assist: this.playersIndex[elem.player_1_id],
          time: elem.time,
          quarter: this.getQuarterName(elem.quarter),
          result: {
            home: prevResult ? prevResult.home + (isHome ? elem.points : 0) : 0,
            visitor: prevResult
              ? prevResult.visitor + (!isHome ? elem.points : 0)
              : 0,
          },
        };
        if (message.team) {
          message.team.isHome = isHome;
        }
        acum.push(message);
      }
      return acum;
    }, []);

    return parsedMessages;
  }

  private getLastResult(fromPosition = 0) {
    const events = this._events.slice(0, fromPosition);
    return this.statsService.calculateResult(events, this._teams);
  }

  private clearMarkers() {
    let marker;
    while ((marker = this.markersList.pop())) {
      marker.remove();
    }
    this.markersLayer.clearLayers();
  }

  private getQuarterName(quarterCode) {
    let quarter;
    if (quarterCode === 1) {
      quarter = `${quarterCode}C`;
    } else if (quarterCode > 2 && quarterCode < 6) {
      quarter = `${quarterCode - 1}C`;
    } else if (quarterCode >= 10) {
      quarter = `P${quarterCode - 9}`;
    }
    return quarter;
  }
}



// WEBPACK FOOTER //
// ./src/app/map/map-raw/map-raw.component.ts